import Context from "../app.context";
import Translator from "../utils/translator";
import { DriveStore } from "../store/reducers";
import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import * as logger from "../utils/logger";
import { getObjectCopy } from "../utils/vanilla";
import { getTranslatedString } from "../utils/helpers";
import { TranslatableDisplayStrings } from "hos-tools";

// needed because OpenCab translations need to be synchronous, and new translations need to be fetched when any user is added or language is changed
export class OpenCabTranslator {
    private readonly translationsByLanguage: Map<string, { [key: string]: string }> = new Map();

    constructor(
        private readonly store: DriveStore,
        private readonly getDriverAndCoDrivers: () => { driver?: DeepReadonly<IUser>; coDrivers: DeepReadonly<IUser>[] },
        private readonly getContext: (userName?: string) => Context
    ) {
        dispatcher.off(DispatcherEvents.UserUpdateLanguage, undefined, this);
        dispatcher.on(DispatcherEvents.UserUpdateLanguage, this.updateTranslations.bind(this));
    }

    translate(str: string, translator: Translator): string {
        if (!this.hasLanguage(translator.language)) {
            logger.log(logger.Source.OPENCAB, `Translate function called before language '${ translator.language }' was added`);
            void this.addLanguage(translator);
            return `**${ str }`;
        }
        let result = getTranslatedString(str, this.translationsByLanguage.get(translator.language) ?? {}, translator.language);
        if (this.isUntranslated(result)) {
            logger.log(logger.Source.OPENCAB, `Translation for ${ str } missing for ${ translator.language }.`);
            void this.addLanguage(translator);
        }
        return result;
    }

    private hasString(str: string, language: string) {
        return language === "en" || (this.translationsByLanguage.get(language) ?? {})[str] !== undefined;
    }

    private hasLanguage(language: string) {
        return language === "en" || this.translationsByLanguage.has(language);
    }

    private isUntranslated(str: string) {
        return str.substring(0, 2) === "**";
    }

    private async addLanguage(translator: Translator): Promise<boolean> {
        try {
            let untranslatedStrings = this.hasLanguage(translator.language)
                ? Object.keys(TranslatableDisplayStrings).map(k => TranslatableDisplayStrings[k]).filter(str => !this.hasString(str, translator.language))
                : Object.keys(TranslatableDisplayStrings).map(k => TranslatableDisplayStrings[k]);
            const translatedStrings = await translator.translate(untranslatedStrings);
            let translationsForThisLanguage = this.translationsByLanguage.get(translator.language) ?? {};
            untranslatedStrings.forEach((untranslated, index) => !this.isUntranslated(translatedStrings[index]) && (translationsForThisLanguage[untranslated] = translatedStrings[index]));
            this.translationsByLanguage.set(translator.language, translationsForThisLanguage);
            return true;
        } catch (e) {
            logger.log(logger.Source.OPENCAB, `Failed to add language. error:${ e }`);
            return false;
        }
    }

    private updateTranslations() {
        let openCabUsernameMap = getObjectCopy<IIdDictionary<string>>(this.store.getState().openCabUsers);
        let { driver, coDrivers } = this.getDriverAndCoDrivers();
        let openCabUsers: DeepReadonlyObject<IUser>[] = [];
        [driver, ...coDrivers].forEach(u => u !== undefined && openCabUsernameMap[u.name] !== undefined && openCabUsers.push(u));
        let translators = openCabUsers.map(u => this.getContext(u.name).translationService.getTranslator());
        translators.forEach(t => this.addLanguage(t));
    }
}
